import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  activateTriggerCreator,
  completeTriggerCreator,
} from 'creators/triggers';
import { getLocalStorageKey, setLocalStorageKey } from 'utilities/localStorage';
import { getParam } from 'utilities/url';

import { useShouldShowTrigger } from 'hooks/triggers';
import { selectUserIsLogged } from 'selectors/user';

// FtuxDetector props:
// - triggerKey: This key is used in our user triggers and in local storage
//   to hold the information about whether to show the FTUX.
// - launchDate: This prop must be a date in any format understood by moment.js.
//   (ISO 8601 preferred) This is the launch date for the feature. New user
//   accounts created after the launch will not show the FTUX.
// - maxDuration: This prop is a maximum time the FTUX will be shown. After
//   this amount of time from the launch date, it will cease to display. This
//   prop should be in a format parseable by moment's duration constructor:
//   https://momentjs.com/docs/#/durations/creating/
// - children: This is not a prop, but the actual children of this component.
//   This component uses the children-as-a-function React pattern to pass in
//   props the child function will need. An example:
//
// <FtuxDetector
//   triggerKey={SHOW_FIRST_CAPTION_EXPERIENCE}
//   launchDate='2021-03-22'
// >
//   {({ ftuxEnabled, endFtux }) => (
//     <CcNewExperienceWrapper
//       showNewExperience={ftuxEnabled}
//       endNewExperience={endFtux}
//       controlBarExpanded={controlBarExpanded}
//     />
// </FtuxDetector>

export default function FtuxDetector({
  triggerKey,
  launchDate,
  maxDuration,
  children,
}) {
  const dispatch = useDispatch();

  const [, setFtuxClosed] = useState(false);
  const isLoggedIn = useSelector(selectUserIsLogged);
  let userCreatedAt = useSelector(state => state.user.current.createdAt);
  const showFtuxLoggedIn = useShouldShowTrigger(triggerKey);
  const lsKey = `triggers:${triggerKey}`;

  useEffect(() => {
    // Allows us to see the ftux at any time
    const override = getParam('ftuxOverride');

    if (override === triggerKey) {
      dispatch(activateTriggerCreator(triggerKey));
      setLocalStorageKey(lsKey, {
        complete: false,
        show: true,
      });
    }
  }, [dispatch, triggerKey, lsKey]);

  let ftuxEnabled = false;

  if (launchDate) {
    launchDate = moment(launchDate);
    userCreatedAt = moment(userCreatedAt);
  }

  if (maxDuration) {
    maxDuration = moment.duration(maxDuration);
  }

  if (launchDate && userCreatedAt && userCreatedAt > launchDate) {
    ftuxEnabled = false;
  } else if (
    launchDate &&
    maxDuration &&
    moment().diff(launchDate, 'seconds') > maxDuration.asSeconds()
  ) {
    ftuxEnabled = false;
    // console.log(`FTUX ${triggerKey} past max duration. Consider removing.`);
  } else if (isLoggedIn) {
    ftuxEnabled = showFtuxLoggedIn;
  } else {
    const showFtux = getLocalStorageKey(lsKey);

    if (typeof showFtux === 'undefined' || showFtux?.show) {
      ftuxEnabled = true;
    } else if (!showFtux) {
      setLocalStorageKey(lsKey, {
        complete: false,
        show: true,
      });

      ftuxEnabled = true;
    }
  }

  const endFtux = async () => {
    if (isLoggedIn) {
      await dispatch(completeTriggerCreator(triggerKey));
    } else {
      setLocalStorageKey(lsKey, {
        complete: true,
        show: false,
      });
    }

    setFtuxClosed(true);
  };

  return <>{children({ ftuxEnabled, endFtux })}</>;
}
